import { call, put, takeLatest, CallEffect, PutEffect, ForkEffect } from 'redux-saga/effects';
import { GalleryTypes, GalleryActions } from '../redux/GalleryRedux';
import { api } from './api';

// Load gallery data
function* loadGallerySaga(action: any): Generator<CallEffect<any> | PutEffect<any>, void, any> {
  try {
    const { filters } = action;
    
    // Build query parameters
    const params = new URLSearchParams();
    if (filters.searchQuery) params.append('search', filters.searchQuery);
    if (filters.selectedCategories.length > 0) {
      filters.selectedCategories.forEach((cat: string) => params.append('category', cat));
    }
    if (filters.dateRange.start) params.append('startDate', filters.dateRange.start);
    if (filters.dateRange.end) params.append('endDate', filters.dateRange.end);
    if (filters.sortBy) params.append('sortBy', filters.sortBy);
    
    const queryString = params.toString();
    const url = queryString ? `/gallery?${queryString}` : '/gallery';
    
    const response = yield call(api.get, url);
    
    if (response.ok) {
      const { data, pagination } = response.data;
      yield put(GalleryActions.loadGallerySuccess(data, pagination));
    } else {
      yield put(GalleryActions.loadGalleryFailure(response.problem || 'Failed to load gallery'));
    }
  } catch (error: any) {
    console.error('Gallery load error:', error);
    yield put(GalleryActions.loadGalleryFailure(error.message || 'Network error'));
  }
}

// Upload image
function* uploadImageSaga(action: any): Generator<CallEffect<any> | PutEffect<any>, void, any> {
  try {
    const { image, goalId } = action;
    
    // Create form data for file upload
    const formData = new FormData();
    formData.append('image', image.file);
    formData.append('title', image.title);
    formData.append('description', image.description);
    formData.append('category', image.category);
    formData.append('goalId', goalId);
    
    const response = yield call(api.post, `/goals/${goalId}/images`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    
    if (response.ok) {
      const uploadedImage = response.data;
      yield put(GalleryActions.uploadImageSuccess(uploadedImage));
    } else {
      yield put(GalleryActions.uploadImageFailure(response.problem || 'Failed to upload image'));
    }
  } catch (error: any) {
    console.error('Image upload error:', error);
    yield put(GalleryActions.uploadImageFailure(error.message || 'Network error'));
  }
}

// Delete image
function* deleteImageSaga(action: any): Generator<CallEffect<any> | PutEffect<any>, void, any> {
  try {
    const { imageId } = action;
    
    const response = yield call(api.delete, `/images/${imageId}`);
    
    if (response.ok) {
      yield put(GalleryActions.deleteImageSuccess(imageId));
    } else {
      yield put(GalleryActions.deleteImageFailure(response.problem || 'Failed to delete image'));
    }
  } catch (error: any) {
    console.error('Image delete error:', error);
    yield put(GalleryActions.deleteImageFailure(error.message || 'Network error'));
  }
}

// Toggle favorite
function* toggleFavoriteSaga(action: any): Generator<CallEffect<any> | PutEffect<any>, void, any> {
  try {
    const { imageId } = action;
    
    // Get current image to check favorite status
    const currentImage = yield call(api.get, `/images/${imageId}`);
    if (!currentImage.ok) {
      yield put(GalleryActions.toggleFavoriteFailure('Failed to get image'));
      return;
    }
    
    const isCurrentlyFavorite = currentImage.data.isFavorite;
    const newFavoriteStatus = !isCurrentlyFavorite;
    
    const response = yield call(api.patch, `/images/${imageId}`, {
      isFavorite: newFavoriteStatus
    });
    
    if (response.ok) {
      yield put(GalleryActions.toggleFavoriteSuccess(imageId, newFavoriteStatus));
    } else {
      yield put(GalleryActions.toggleFavoriteFailure(response.problem || 'Failed to update favorite'));
    }
  } catch (error: any) {
    console.error('Toggle favorite error:', error);
    yield put(GalleryActions.toggleFavoriteFailure(error.message || 'Network error'));
  }
}

// Export sagas
export default function* gallerySaga(): Generator<ForkEffect<any>, void, any> {
  yield takeLatest(GalleryTypes.LOAD_GALLERY_REQUEST, loadGallerySaga);
  yield takeLatest(GalleryTypes.UPLOAD_IMAGE_REQUEST, uploadImageSaga);
  yield takeLatest(GalleryTypes.DELETE_IMAGE_REQUEST, deleteImageSaga);
  yield takeLatest(GalleryTypes.TOGGLE_FAVORITE, toggleFavoriteSaga);
}
